import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  FolderOpen, FolderPlus, Upload, Search, Trash2, Edit3, Check, X,
  ChevronRight, ChevronDown, MoreVertical, Image as ImageIcon, 
  RefreshCw, ArrowRight, Download, Calendar, HardDrive, Link2,
  AlertTriangle, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// System folders that cannot be deleted
const SYSTEM_FOLDERS = ['All', 'Homepage', 'Services', 'Projects', 'Sectors', 'Team', 'Logos', 'Other'];

export const ImageLibraryManager = () => {
  const { toast } = useToast();
  
  // Core state
  const [folders, setFolders] = useState([]);
  const [folderCounts, setFolderCounts] = useState({});
  const [images, setImages] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [renamingImage, setRenamingImage] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({ total: 0, skip: 0, limit: 50, hasMore: false });
  const [loadingMore, setLoadingMore] = useState(false);
  
  const fileInputRef = useRef(null);
  const gridContainerRef = useRef(null);

  // Fetch folders and images
  const fetchFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/images?limit=1`);
      if (response.data.folders) {
        setFolders(response.data.folders);
        setFolderCounts(response.data.folderCounts || {});
      }
    } catch (error) {
      console.error('[ImageLibrary] Error fetching folders:', error);
    }
  }, []);

  const fetchImages = useCallback(async (folder = selectedFolder, search = searchQuery, append = false) => {
    setLoading(!append);
    if (append) setLoadingMore(true);
    
    try {
      const params = new URLSearchParams();
      if (folder && folder !== 'All') params.append('folder', folder);
      if (search) params.append('search', search);
      params.append('skip', append ? (pagination.skip + pagination.limit).toString() : '0');
      params.append('limit', '50');
      params.append('sort_by', sortBy === 'newest' || sortBy === 'oldest' ? 'uploadedAt' : 'originalName');
      params.append('sort_order', sortBy === 'oldest' || sortBy === 'name_asc' ? 'asc' : 'desc');
      
      const response = await axios.get(`${API}/images?${params.toString()}`);
      
      if (append) {
        setImages(prev => [...prev, ...(response.data.images || [])]);
      } else {
        setImages(response.data.images || []);
      }
      
      setFolders(response.data.folders || []);
      setFolderCounts(response.data.folderCounts || {});
      setPagination(response.data.pagination || { total: 0, skip: 0, limit: 50, hasMore: false });
    } catch (error) {
      console.error('[ImageLibrary] Error fetching images:', error);
      toast({ 
        title: "Failed to load images", 
        description: error.response?.data?.detail || error.message, 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedFolder, searchQuery, sortBy, pagination.skip, pagination.limit, toast]);

  // Initial load
  useEffect(() => {
    fetchImages(selectedFolder, searchQuery, false);
  }, [selectedFolder, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchImages(selectedFolder, searchQuery, false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle folder click
  const handleFolderClick = useCallback((folder) => {
    setSelectedFolder(folder);
    setSelectedImage(null);
    setShowDetailsPanel(false);
    // Images will be fetched by useEffect
  }, []);

  // Handle image click
  const handleImageClick = useCallback((img, e) => {
    e.stopPropagation();
    setSelectedImage(img);
    setShowDetailsPanel(true);
    setContextMenu(null);
  }, []);

  // Handle image context menu
  const handleImageContextMenu = useCallback((img, e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      image: img,
      x: e.clientX,
      y: e.clientY
    });
  }, []);

  // Handle folder context menu
  const handleFolderContextMenu = useCallback((folder, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!SYSTEM_FOLDERS.includes(folder)) {
      setFolderContextMenu({
        folder,
        x: e.clientX,
        y: e.clientY
      });
    }
  }, []);

  // Close context menus on click outside
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
      setFolderContextMenu(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Upload handler
  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const targetFolder = selectedFolder !== 'All' ? selectedFolder : 'Other';
    let successCount = 0;
    let failCount = 0;
    
    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', targetFolder);
      
      try {
        await axios.post(`${API}/images/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        successCount++;
      } catch (error) {
        console.error('[ImageLibrary] Upload error:', error);
        failCount++;
      }
    }
    
    setUploading(false);
    
    if (successCount > 0) {
      toast({ 
        title: "Upload Complete", 
        description: `${successCount} uploaded${failCount > 0 ? `, ${failCount} failed` : ''}` 
      });
      fetchImages(selectedFolder, searchQuery, false);
    } else {
      toast({ 
        title: "Upload Failed", 
        description: "No files were uploaded successfully", 
        variant: "destructive" 
      });
    }
  }, [selectedFolder, searchQuery, fetchImages, toast]);

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await axios.post(`${API}/images/folders`, { name: newFolderName.trim() });
      toast({ title: "Folder Created", description: `"${newFolderName}" created successfully` });
      setNewFolderName('');
      setShowNewFolderInput(false);
      fetchFolders();
    } catch (error) {
      toast({ 
        title: "Create Failed", 
        description: error.response?.data?.detail || "Could not create folder", 
        variant: "destructive" 
      });
    }
  }, [newFolderName, fetchFolders, toast]);

  // Rename folder
  const handleRenameFolder = useCallback(async (oldName, newName) => {
    if (!newName.trim() || newName === oldName) {
      setRenamingFolder(null);
      return;
    }
    
    try {
      await axios.put(`${API}/images/folders/${encodeURIComponent(oldName)}`, { name: newName.trim() });
      toast({ title: "Folder Renamed", description: `Renamed to "${newName}"` });
      setRenamingFolder(null);
      if (selectedFolder === oldName) {
        setSelectedFolder(newName.trim());
      }
      fetchFolders();
      fetchImages(selectedFolder === oldName ? newName.trim() : selectedFolder, searchQuery, false);
    } catch (error) {
      toast({ 
        title: "Rename Failed", 
        description: error.response?.data?.detail || "Could not rename folder", 
        variant: "destructive" 
      });
      setRenamingFolder(null);
    }
  }, [selectedFolder, searchQuery, fetchFolders, fetchImages, toast]);

  // Delete folder
  const handleDeleteFolder = useCallback(async (folder, action = 'move') => {
    try {
      await axios.delete(`${API}/images/folders/${encodeURIComponent(folder)}?action=${action}`);
      toast({ title: "Folder Deleted", description: action === 'move' ? "Assets moved to Other" : "Folder and assets deleted" });
      setDeleteConfirm(null);
      setFolderContextMenu(null);
      if (selectedFolder === folder) {
        setSelectedFolder('All');
      }
      fetchFolders();
      fetchImages('All', searchQuery, false);
    } catch (error) {
      toast({ 
        title: "Delete Failed", 
        description: error.response?.data?.detail || "Could not delete folder", 
        variant: "destructive" 
      });
    }
  }, [selectedFolder, searchQuery, fetchFolders, fetchImages, toast]);

  // Rename image
  const handleRenameImage = useCallback(async (imageId, newName) => {
    if (!newName.trim()) {
      setRenamingImage(null);
      return;
    }
    
    try {
      await axios.patch(`${API}/images/${imageId}/rename`, { filename: newName.trim() });
      toast({ title: "Image Renamed", description: `Renamed to "${newName}"` });
      setRenamingImage(null);
      fetchImages(selectedFolder, searchQuery, false);
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => prev ? { ...prev, originalName: newName.trim() } : null);
      }
    } catch (error) {
      toast({ 
        title: "Rename Failed", 
        description: error.response?.data?.detail || "Could not rename image", 
        variant: "destructive" 
      });
      setRenamingImage(null);
    }
  }, [selectedFolder, searchQuery, selectedImage, fetchImages, toast]);

  // Move image to folder
  const handleMoveImage = useCallback(async (imageId, targetFolder) => {
    try {
      await axios.patch(`${API}/images/${imageId}/move`, { folder: targetFolder });
      toast({ title: "Image Moved", description: `Moved to "${targetFolder}"` });
      setMoveTarget(null);
      setContextMenu(null);
      fetchImages(selectedFolder, searchQuery, false);
    } catch (error) {
      toast({ 
        title: "Move Failed", 
        description: error.response?.data?.detail || "Could not move image", 
        variant: "destructive" 
      });
    }
  }, [selectedFolder, searchQuery, fetchImages, toast]);

  // Delete image
  const handleDeleteImage = useCallback(async (img, force = false) => {
    const usageCount = img.usedIn?.length || 0;
    
    if (usageCount > 0 && !force) {
      setDeleteConfirm({ type: 'image', item: img });
      return;
    }
    
    try {
      await axios.delete(`${API}/images/${img.id}${force ? '?force=true' : ''}`);
      toast({ title: "Image Deleted", description: "Image removed from library" });
      setDeleteConfirm(null);
      setContextMenu(null);
      if (selectedImage?.id === img.id) {
        setSelectedImage(null);
        setShowDetailsPanel(false);
      }
      fetchImages(selectedFolder, searchQuery, false);
    } catch (error) {
      toast({ 
        title: "Delete Failed", 
        description: error.response?.data?.detail || "Could not delete image", 
        variant: "destructive" 
      });
    }
  }, [selectedFolder, searchQuery, selectedImage, fetchImages, toast]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get image URL
  const getImageUrl = (img) => {
    if (!img?.url) return '';
    return img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`;
  };

  // Breadcrumb
  const breadcrumb = useMemo(() => {
    const parts = [{ name: 'All Images', folder: 'All' }];
    if (selectedFolder !== 'All') {
      parts.push({ name: selectedFolder, folder: selectedFolder });
    }
    return parts;
  }, [selectedFolder]);

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Image Library</h2>
          <p className="text-gray-500">Manage all media assets across your website</p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/mp4,video/webm"
            multiple
            className="hidden"
            onChange={(e) => {
              handleUpload(e.target.files);
              e.target.value = '';
            }}
          />
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload</>
            )}
          </Button>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        {breadcrumb.map((item, idx) => (
          <React.Fragment key={item.folder}>
            {idx > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
            <button
              onClick={() => handleFolderClick(item.folder)}
              className={`hover:text-blue-600 ${selectedFolder === item.folder ? 'text-blue-600 font-medium' : 'text-gray-600'}`}
            >
              {item.name}
            </button>
          </React.Fragment>
        ))}
        <span className="text-gray-400 ml-2">({pagination.total} items)</span>
      </div>

      {/* Main Layout */}
      <div className="flex gap-4 flex-1 min-h-0">
        {/* Folder Sidebar */}
        <div className="w-52 shrink-0 flex flex-col">
          <Card className="border shadow-sm flex-1 overflow-hidden flex flex-col">
            <CardHeader className="py-3 px-4 border-b bg-gray-50">
              <CardTitle className="text-xs font-semibold text-gray-500 uppercase flex items-center justify-between">
                Folders
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-blue-100"
                  onClick={() => setShowNewFolderInput(true)}
                  title="Create folder"
                >
                  <FolderPlus className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 flex-1 overflow-y-auto">
              {/* New folder input */}
              {showNewFolderInput && (
                <div className="mb-2 p-2 bg-blue-50 rounded-lg">
                  <Input
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateFolder();
                      if (e.key === 'Escape') {
                        setShowNewFolderInput(false);
                        setNewFolderName('');
                      }
                    }}
                    placeholder="Folder name"
                    className="h-8 text-sm mb-2"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    <Button size="sm" className="h-7 text-xs flex-1" onClick={handleCreateFolder}>
                      Create
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
                      setShowNewFolderInput(false);
                      setNewFolderName('');
                    }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Folder list */}
              <div className="space-y-0.5">
                {folders.map((folder) => {
                  const isSystem = SYSTEM_FOLDERS.includes(folder);
                  const isSelected = selectedFolder === folder;
                  const isRenaming = renamingFolder === folder;
                  
                  return (
                    <div 
                      key={folder}
                      className="relative"
                      onContextMenu={(e) => handleFolderContextMenu(folder, e)}
                    >
                      {isRenaming ? (
                        <Input
                          defaultValue={folder}
                          className="h-9 text-sm"
                          autoFocus
                          onBlur={(e) => handleRenameFolder(folder, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleRenameFolder(folder, e.currentTarget.value);
                            }
                            if (e.key === 'Escape') setRenamingFolder(null);
                          }}
                        />
                      ) : (
                        <button
                          onClick={() => handleFolderClick(folder)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors
                            ${isSelected 
                              ? 'bg-blue-600 text-white' 
                              : 'hover:bg-gray-100 text-gray-700'}`}
                        >
                          <span className="flex items-center gap-2 truncate">
                            <FolderOpen className="w-4 h-4 shrink-0" />
                            <span className="truncate">{folder}</span>
                          </span>
                          <span className={`text-xs font-medium ml-2 shrink-0 ${isSelected ? 'text-blue-100' : 'text-gray-400'}`}>
                            {folderCounts[folder] || 0}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Search and Sort Bar */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search images..."
                className="pl-9"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name_asc">Name A-Z</option>
              <option value="name_desc">Name Z-A</option>
            </select>
          </div>

          {/* Grid Container */}
          <div 
            ref={gridContainerRef}
            className="flex-1 overflow-y-auto rounded-lg bg-gray-50 p-4"
            style={{ minHeight: '400px' }}
          >
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : images.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-lg font-medium">No images found</p>
                <p className="text-sm">Upload images or try a different folder</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {images.map((img) => (
                    <ImageCard
                      key={img.id}
                      image={img}
                      isSelected={selectedImage?.id === img.id}
                      isRenaming={renamingImage === img.id}
                      onClick={handleImageClick}
                      onContextMenu={handleImageContextMenu}
                      onRename={handleRenameImage}
                      onCancelRename={() => setRenamingImage(null)}
                      getImageUrl={getImageUrl}
                      formatFileSize={formatFileSize}
                    />
                  ))}
                </div>
                
                {/* Load More */}
                {pagination.hasMore && (
                  <div className="text-center py-6">
                    <Button
                      variant="outline"
                      onClick={() => fetchImages(selectedFolder, searchQuery, true)}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                      ) : (
                        <>Load More ({pagination.total - images.length} remaining)</>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Details Panel */}
        {showDetailsPanel && selectedImage && (
          <DetailsPanel
            image={selectedImage}
            folders={folders.filter(f => f !== 'All')}
            onClose={() => {
              setShowDetailsPanel(false);
              setSelectedImage(null);
            }}
            onRename={() => setRenamingImage(selectedImage.id)}
            onMove={handleMoveImage}
            onDelete={() => handleDeleteImage(selectedImage)}
            getImageUrl={getImageUrl}
            formatFileSize={formatFileSize}
            formatDate={formatDate}
          />
        )}
      </div>

      {/* Context Menu for Images */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        >
          <ContextMenuItem onClick={() => {
            setRenamingImage(contextMenu.image.id);
            setContextMenu(null);
          }}>
            <Edit3 className="w-4 h-4 mr-2" /> Rename
          </ContextMenuItem>
          <ContextMenuItem onClick={() => setMoveTarget(contextMenu.image)}>
            <ArrowRight className="w-4 h-4 mr-2" /> Move to...
          </ContextMenuItem>
          <ContextMenuItem 
            onClick={() => handleDeleteImage(contextMenu.image)}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </ContextMenuItem>
        </ContextMenu>
      )}

      {/* Context Menu for Folders */}
      {folderContextMenu && (
        <ContextMenu
          x={folderContextMenu.x}
          y={folderContextMenu.y}
          onClose={() => setFolderContextMenu(null)}
        >
          <ContextMenuItem onClick={() => {
            setRenamingFolder(folderContextMenu.folder);
            setFolderContextMenu(null);
          }}>
            <Edit3 className="w-4 h-4 mr-2" /> Rename
          </ContextMenuItem>
          <ContextMenuItem 
            onClick={() => {
              const count = folderCounts[folderContextMenu.folder] || 0;
              if (count > 0) {
                setDeleteConfirm({ type: 'folder', item: folderContextMenu.folder, count });
              } else {
                handleDeleteFolder(folderContextMenu.folder);
              }
              setFolderContextMenu(null);
            }}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </ContextMenuItem>
        </ContextMenu>
      )}

      {/* Move Image Modal */}
      {moveTarget && (
        <MoveModal
          image={moveTarget}
          folders={folders.filter(f => f !== 'All' && f !== moveTarget.folder)}
          onMove={(folder) => handleMoveImage(moveTarget.id, folder)}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          item={deleteConfirm}
          onConfirm={(force) => {
            if (deleteConfirm.type === 'image') {
              handleDeleteImage(deleteConfirm.item, force);
            } else {
              handleDeleteFolder(deleteConfirm.item, force ? 'delete' : 'move');
            }
          }}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Image Card Component - Pure CSS hover, no state changes
const ImageCard = React.memo(({ 
  image, 
  isSelected, 
  isRenaming,
  onClick, 
  onContextMenu,
  onRename,
  onCancelRename,
  getImageUrl,
  formatFileSize 
}) => {
  const inputRef = useRef(null);

  return (
    <div
      className={`
        relative bg-white rounded-lg overflow-hidden shadow-sm cursor-pointer
        transition-shadow duration-150 ease-out
        ${isSelected ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-md'}
      `}
      onClick={(e) => onClick(image, e)}
      onContextMenu={(e) => onContextMenu(image, e)}
    >
      {/* Image Container - Fixed aspect ratio */}
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        <img 
          src={getImageUrl(image)} 
          alt={image.originalName}
          className="w-full h-full object-cover image-card-img"
          loading="lazy"
          draggable={false}
        />
        {/* Pure CSS hover overlay */}
        <div className="absolute inset-0 bg-black opacity-0 hover:opacity-20 transition-opacity duration-150 pointer-events-none" />
        
        {/* Usage badge */}
        {image.usedIn?.length > 0 && (
          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
            <Link2 className="w-3 h-3" />
            {image.usedIn.length}
          </div>
        )}
        
        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center pointer-events-none">
            <Check className="w-10 h-10 text-white drop-shadow-lg" />
          </div>
        )}
      </div>
      
      {/* Info footer */}
      <div className="p-2">
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            defaultValue={image.originalName.replace(/\.[^/.]+$/, '')}
            className="w-full text-xs border rounded px-1 py-0.5"
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              const ext = image.originalName.match(/\.[^/.]+$/)?.[0] || '';
              onRename(image.id, e.target.value + ext);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                const ext = image.originalName.match(/\.[^/.]+$/)?.[0] || '';
                onRename(image.id, e.currentTarget.value + ext);
              }
              if (e.key === 'Escape') onCancelRename();
            }}
          />
        ) : (
          <p className="text-xs font-medium text-gray-800 truncate" title={image.originalName}>
            {image.originalName}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-0.5">{formatFileSize(image.size)}</p>
      </div>
    </div>
  );
});

ImageCard.displayName = 'ImageCard';

// Details Panel Component
const DetailsPanel = ({ 
  image, 
  folders,
  onClose, 
  onRename,
  onMove,
  onDelete,
  getImageUrl, 
  formatFileSize, 
  formatDate 
}) => {
  const [selectedMoveFolder, setSelectedMoveFolder] = useState('');

  return (
    <div className="w-80 shrink-0 bg-white rounded-lg shadow-lg border overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between bg-gray-50 shrink-0">
        <h3 className="font-semibold text-gray-800 truncate flex-1">Details</h3>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Preview - smaller height */}
      <div className="h-40 bg-gray-100 relative shrink-0">
        <img 
          src={getImageUrl(image)} 
          alt={image.originalName}
          className="w-full h-full object-contain"
        />
      </div>
      
      {/* Metadata - scrollable */}
      <div className="p-3 space-y-2 flex-1 overflow-y-auto min-h-0">
        <div>
          <label className="text-xs text-gray-500 uppercase font-medium">Filename</label>
          <p className="text-sm font-medium text-gray-800 break-all">{image.originalName}</p>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1">
              <HardDrive className="w-3 h-3" /> Size
            </label>
            <p className="text-sm text-gray-700">{formatFileSize(image.size)}</p>
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1">
              <FolderOpen className="w-3 h-3" /> Folder
            </label>
            <p className="text-sm text-gray-700">{image.folder}</p>
          </div>
        </div>
        
        <div>
          <label className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Uploaded
          </label>
          <p className="text-sm text-gray-700">{formatDate(image.uploadedAt || image.createdAt)}</p>
        </div>
        
        <div>
          <label className="text-xs text-gray-500 uppercase font-medium flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Used In
          </label>
          {image.usedIn?.length > 0 ? (
            <ul className="text-sm text-gray-700 mt-1 space-y-1">
              {image.usedIn.map((usage, idx) => (
                <li key={idx} className="flex items-center gap-1 text-xs bg-gray-100 rounded px-2 py-1">
                  {usage.type}: {usage.name || usage.itemId}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">Not used anywhere</p>
          )}
        </div>

        {/* Move to folder */}
        <div>
          <label className="text-xs text-gray-500 uppercase font-medium">Move to Folder</label>
          <div className="flex gap-2 mt-1">
            <select
              value={selectedMoveFolder}
              onChange={(e) => setSelectedMoveFolder(e.target.value)}
              className="flex-1 text-sm border rounded px-2 py-1.5"
            >
              <option value="">Select folder...</option>
              {folders.filter(f => f !== image.folder).map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <Button 
              size="sm" 
              disabled={!selectedMoveFolder}
              onClick={() => {
                onMove(image.id, selectedMoveFolder);
                setSelectedMoveFolder('');
              }}
            >
              Move
            </Button>
          </div>
        </div>
      </div>
      
      {/* Actions - fixed at bottom */}
      <div className="p-3 border-t bg-gray-50 space-y-2 shrink-0">
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={onRename}>
          <Edit3 className="w-4 h-4 mr-2" /> Rename
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={onDelete}
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete
        </Button>
      </div>
    </div>
  );
};

// Context Menu Component
const ContextMenu = ({ x, y, children, onClose }) => {
  return (
    <div 
      className="fixed z-50 bg-white rounded-lg shadow-xl border py-1 min-w-[160px]"
      style={{ 
        left: Math.min(x, window.innerWidth - 180), 
        top: Math.min(y, window.innerHeight - 150) 
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

const ContextMenuItem = ({ children, onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 ${className}`}
  >
    {children}
  </button>
);

// Move Modal Component
const MoveModal = ({ image, folders, onMove, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-80 p-4" onClick={(e) => e.stopPropagation()}>
      <h3 className="font-semibold text-gray-800 mb-4">Move "{image.originalName}"</h3>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {folders.map(folder => (
          <button
            key={folder}
            onClick={() => onMove(folder)}
            className="w-full text-left px-3 py-2 rounded hover:bg-blue-50 hover:text-blue-600 flex items-center gap-2"
          >
            <FolderOpen className="w-4 h-4" />
            {folder}
          </button>
        ))}
      </div>
      <Button variant="outline" className="w-full mt-4" onClick={onClose}>Cancel</Button>
    </div>
  </div>
);

// Delete Confirmation Modal
const DeleteConfirmModal = ({ item, onConfirm, onCancel }) => {
  const isImage = item.type === 'image';
  const usageCount = isImage ? (item.item.usedIn?.length || 0) : item.count;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl w-96 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="font-semibold text-gray-800">
            {isImage ? 'Delete Image?' : 'Delete Folder?'}
          </h3>
        </div>
        
        {isImage ? (
          <div className="mb-4">
            <p className="text-gray-600 mb-2">
              This image is used in {usageCount} place{usageCount !== 1 ? 's' : ''}:
            </p>
            <ul className="text-sm bg-gray-50 rounded p-2 space-y-1 max-h-32 overflow-y-auto">
              {item.item.usedIn?.map((usage, idx) => (
                <li key={idx} className="text-gray-700">• {usage.type}: {usage.name || usage.itemId}</li>
              ))}
            </ul>
            <p className="text-sm text-red-600 mt-2">
              Deleting will remove references and may break content.
            </p>
          </div>
        ) : (
          <p className="text-gray-600 mb-4">
            This folder contains {usageCount} image{usageCount !== 1 ? 's' : ''}. What would you like to do?
          </p>
        )}
        
        <div className="space-y-2">
          {!isImage && usageCount > 0 && (
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700" 
              onClick={() => onConfirm(false)}
            >
              Move images to Other and delete folder
            </Button>
          )}
          <Button 
            variant="destructive" 
            className="w-full" 
            onClick={() => onConfirm(true)}
          >
            {isImage ? 'Delete anyway' : (usageCount > 0 ? 'Delete folder and all images' : 'Delete folder')}
          </Button>
          <Button variant="outline" className="w-full" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add CSS to prevent image flicker
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  .image-card-img {
    will-change: auto;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }
`;
document.head.appendChild(styleSheet);

export default ImageLibraryManager;
